<template>
  <UiFileUploadBase
    :title="title"
    :files="displayFiles"
    :allowedFileTypes="allowedFileTypes"
    :fileNameFormat="fileNameFormat"
    :maxSizeBytes="maxSizeBytes"
    :maxFiles="maxFiles"
    :acceptLabel="resolvedAcceptLabel"
    :dragText="dragText"
    :orClickText="orClickText"
    :invalidTypeText="invalidTypeText"
    :fileTooLargeText="fileTooLargeText"
    :resolveFileUrl="resolveFileUrl"
    @on-file-add="onFileAdd"
    @on-file-remove="onFileRemove" />
</template>

<script lang="ts">
import { storage } from '@/firebase/init'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import type { IFileBase } from '@team-milva/lab-models/files'
import { t } from '@/locales'
import { reportError } from '@/globals/typescript/utils/reportError'
import { useToast } from '@/globals/typescript/utils/toast'
import type { PropType } from 'vue'
import type {
  TAllowedFileType,
  TFileNameFormat,
} from '@team-milva/lab-components'

export default {
  name: 'UiFileUpload',

  emits: ['update:modelValue', 'on-file-selected', 'on-pending-change'],

  props: {
    modelValue: {
      type: Array as PropType<IFileBase[]>,
      required: true,
    },
    uploadPath: {
      type: String,
      required: false,
      default: '',
    },
    allowedFileTypes: {
      type: Array as PropType<TAllowedFileType[]>,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    fileNameFormat: {
      type: String as PropType<TFileNameFormat>,
      default: '' as TFileNameFormat,
    },
    maxSizeBytes: {
      type: Number,
      default: 15 * 1024 * 1024,
    },
    maxFiles: {
      type: Number,
      default: 0,
    },
    acceptLabel: {
      type: String,
      default: '',
    },
    dragText: {
      type: String,
      default: () => t('file-upload.drag-file-here'),
    },
    orClickText: {
      type: String,
      default: () => t('file-upload.or-click-to-select'),
    },
    invalidTypeText: {
      type: String,
      default: () => t('error.file.invalid-file-type'),
    },
    fileTooLargeText: {
      type: String,
      default: () => t('file-upload.file-too-large'),
    },
  },

  data() {
    return {
      pendingFiles: [] as File[],
    }
  },

  computed: {
    resolvedAcceptLabel(): string {
      if (this.acceptLabel) return this.acceptLabel
      const types = this.allowedFileTypes.map((x) => x.toUpperCase()).join(', ')
      const mb = Math.round(this.maxSizeBytes / (1024 * 1024))
      return `${types} · ${t('file-upload.max')} ${mb} MB`
    },

    // Merge saved files with pending (not yet uploaded) files so they appear
    // in the list immediately. The base component holds the blob URL for
    // each pending file, so clicks on them open the local preview.
    displayFiles(): IFileBase[] {
      return [
        ...this.modelValue,
        ...this.pendingFiles.map((x) => ({
          name: x.name,
          path: '',
          contentType: x.type,
          createdAt: 0,
        })),
      ]
    },
  },

  methods: {
    onFileAdd(rawFile: File) {
      this.pendingFiles = [...this.pendingFiles, rawFile]
      this.$emit('on-file-selected', rawFile)
      this.$emit('on-pending-change', this.pendingFiles.length)
    },

    onFileRemove(index: number) {
      if (index < this.modelValue.length) {
        // A saved file — emit updated array without it.
        this.$emit(
          'update:modelValue',
          this.modelValue.filter((_, i) => i !== index)
        )
      } else {
        // A pending file — remove from local list. The base component cleans
        // up the blob URL automatically.
        const pendingIndex = index - this.modelValue.length
        this.pendingFiles = this.pendingFiles.filter(
          (_, i) => i !== pendingIndex
        )
        this.$emit('on-pending-change', this.pendingFiles.length)
      }
    },

    async resolveFileUrl(file: { name: string }): Promise<string> {
      // Pending files are handled by the blob URL in the base component —
      // return empty string to let it fall through to that.
      if (this.pendingFiles.some((x) => x.name === file.name)) return ''

      const record = this.modelValue.find((x) => x.name === file.name)
      if (!record) return ''

      try {
        return await getDownloadURL(storageRef(storage, record.path))
      } catch (error) {
        // Returned bare, this rejection propagated into the base component with nowhere to
        // land: the file silently failed to open and nothing was recorded anywhere. Storage
        // is talked to directly from the browser, so there is no server-side trace either.
        reportError(error, {
          operation: 'storage-resolve-file-url',
          extra: { storagePath: record.path },
        })
        // The base component opens nothing on an empty string, so without this the click
        // is silent — reporting is not user feedback (error-reporting.md).
        useToast().error(this.$t('W: Kunne ikke åbne filen'))
        return ''
      }
    },

    // Called by the parent on form submit. Uploads all pending files to
    // Firebase Storage, emits the updated modelValue, and returns the new
    // complete array so the parent can use it immediately.
    //
    // `overridePath`, when given, is used as the exact upload path for the (single)
    // pending file instead of `uploadPath` + an appended timestamp/random suffix — for
    // resources where the Storage filename should be the owning record's own ID, decided
    // by the caller right before calling this method. Passed as an argument rather than a
    // reactive prop so a caller that regenerates the ID per-submission (e.g. a modal reused
    // for multiple uploads) can't race a prop update that hasn't reached this component yet.
    async uploadPending(overridePath?: string): Promise<IFileBase[]> {
      if (!this.pendingFiles.length) return [...this.modelValue]

      if (overridePath && this.pendingFiles.length > 1) {
        throw new Error(
          'uploadPending: overridePath cannot be used with more than one pending file'
        )
      }
      if (!overridePath && !this.uploadPath) {
        throw new Error('uploadPending: uploadPath or overridePath is required')
      }

      const uploaded: IFileBase[] = []
      for (const file of this.pendingFiles) {
        const timestamp = Date.now()
        const random = Math.random().toString(36).slice(2, 7)
        const path =
          overridePath || `${this.uploadPath}/${timestamp}-${random}-${file.name}`
        try {
          await uploadBytes(storageRef(storage, path), file)
        } catch (error) {
          // The browser uploads straight to Storage, so lab-cloud never sees
          // this — reported here or reported nowhere. A Storage rules denial
          // arrives as code 'storage/unauthorized'.
          reportError(error, {
            operation: 'file-upload',
            extra: {
              path,
              fileName: file.name,
              contentType: file.type,
              sizeBytes: file.size,
              storageErrorCode:
                (error as { code?: string } | null)?.code ?? null,
            },
          })
          throw error
        }
        uploaded.push({
          name: file.name,
          path,
          contentType: file.type,
          createdAt: Date.now(),
        } satisfies IFileBase)
      }

      const newModelValue = [...this.modelValue, ...uploaded]
      this.$emit('update:modelValue', newModelValue)
      this.pendingFiles = []
      this.$emit('on-pending-change', 0)
      return newModelValue
    },
  },
}
</script>

<style>
@reference "../../globals/css/base.css";

.s-file-upload {
  .s-file-upload__dropzone {
    @apply border-border-subtle text-text-primary;

    &.s-file-upload__dropzone--dragging,
    &.s-file-upload__dropzone--hovering {
      @apply border-border-strong bg-surface-muted;
    }
  }

  .s-file-upload__click-text {
    @apply text-text-secondary;
  }

  .s-file-upload__error {
    @apply bg-warning-bg text-warning;
  }

  .s-file-upload__accept-label {
    @apply text-text-secondary;
  }

  .s-file-upload__list-item {
    @apply border-border-subtle bg-surface;
  }

  .s-file-upload__file-icon {
    @apply text-text-muted;
  }

  .s-file-upload__file-name {
    @apply text-text-primary;
  }

  .s-file-upload__file-date {
    @apply text-text-secondary;
  }

  .s-file-upload__remove-btn {
    @apply bg-surface-sunken text-text-primary opacity-75 hover:opacity-90;
  }
}
</style>

